import { OeeBatchRepository } from '@/libs/tpm-analytics/data/oee-batch.repository';
import { buildUnionIntervalsByWorkplace, sumOverlapMinutes, computeIntervalIntersectionMinutes } from '@/libs/tpm-analytics/core/downtime.utils';
import { buildCycleTimeResolver, buildLocationChainsForShifts } from '@/libs/tpm-analytics/core/cycle-time.resolver';
import { computeOeeComponents } from '@/libs/tpm-analytics/core/oee.calculator';
import { startOfProductionDayMs, endOfProductionDayMs, startOfProductionDayIso, endOfProductionDayIso, getTenantTimeZoneIso, getDayStartMinutes } from '@/libs/tpm-analytics/core/production-day.utils';
import { ShiftModel } from '@/libs/Api';
import { ScrapRecordsRepository } from '@/libs/tpm-analytics/data/scrap-records.repository';
import moment from 'moment-timezone';

type Num = number | null;

export interface PanelOeeComponents {
    oee: Num;
    availability: Num;
    performance: Num;
    quality: Num;
    // Surová data pro správnou agregaci při průměrování přes pracoviště
    totalOk: number;
    totalNok: number;
    totalXMinutes: number;
    totalUnplannedMinutes: number;
    totalIdealRuntimeMinutes: number;
}

function floorToFullHourMs(ts: number): number {
    const d = new Date(ts);
    if (d.getMinutes() === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0) return ts;
    d.setMinutes(0, 0, 0);
    return d.getTime();
}

/**
 * Spočítá OEE pro aktuálně běžící směny na jednotlivých pracovištích.
 * - Všechny metriky (Dostupnost, Výkonnost, Kvalita) se počítají pouze z ukončených celých hodin
 * - Data se berou od začátku směny do poslední ukončené celé hodiny (např. při 15:18 pouze do 15:00)
 * - Důvod: záznamy z aktuální hodiny mohou být dostupné až v následující hodině
 * - Data z API se načítají pro celý výrobní den (od dayStart) + následující den, aby byla zajištěna dostupnost všech potřebných dat
 */
export async function getPanelCurrentOeeByWorkplace(filters?: {
    locationIds?: string[];
    workplaceIds?: string[];
    projectIds?: string[];
    shiftTypeIds?: string[];
    shiftTeamTypeIds?: string[];
}, options?: { nowMs?: number }): Promise<Map<string | null, PanelOeeComponents>> {

    const nowMs = typeof options?.nowMs === 'number' && isFinite(options.nowMs!) ? (options!.nowMs as number) : Date.now();
    const cutoffMs = floorToFullHourMs(nowMs);
    
    // Použití moment s časovou zónou pro správný ISO string
    const timezone = getTenantTimeZoneIso();
    const cutoffIso = timezone ? moment(cutoffMs).tz(timezone).format() : moment(cutoffMs).format();
    
    // KLÍČOVÁ LOGIKA: Zjištění, zda jsme mezi půlnocí a dayStart
    // Pokud ano, stále běží VČEREJŠÍ výrobní den!
    // Příklad: při 03:00 a dayStart 06:00 → stále běží výrobní den, který začal včera v 06:00
    const dayStartMinutes = getDayStartMinutes();
    const nowDate = new Date(nowMs);
    const midnightMs = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate()).getTime();
    const dayStartMs = midnightMs + dayStartMinutes * 60000;
    const isBeforeDayStart = nowMs < dayStartMs;
    
    // Pokud jsme před dayStart, použijeme včerejší datum pro určení výrobního dne
    const referenceDayMs = isBeforeDayStart ? nowMs - 24 * 60 * 60 * 1000 : nowMs;
    
    // Načítání dat z API: od začátku aktuálního výrobního dne do konce následujícího výrobního dne
    // Tím zajistíme, že máme dostatek dat pro všechny směny, které mohou zasahovat do aktuálního období
    const productionDayStartMs = startOfProductionDayMs(referenceDayMs);
    const nextProductionDayEndMs = endOfProductionDayMs(referenceDayMs) + 24 * 60 * 60 * 1000; // + další den
    const fromMs = productionDayStartMs;
    const toMs = Math.max(nextProductionDayEndMs, nowMs); // minimálně do teď
    
    // Použití ISO funkcí s podporou časové zóny pro správné určení výrobního dne
    const fromIso = startOfProductionDayIso(referenceDayMs);
    const nextDayMs = referenceDayMs + 24 * 60 * 60 * 1000;
    const toIso = endOfProductionDayIso(referenceDayMs);

    console.log("fetch", fromIso, toIso);

    const { shifts, unplanned, planned } = await OeeBatchRepository.fetchCurrent(fromIso, toIso, {
        locationIds: filters?.locationIds,
        workplaceIds: filters?.workplaceIds,
        projectIds: filters?.projectIds,
        shiftTypeIds: filters?.shiftTypeIds,
        shiftTeamTypeIds: filters?.shiftTeamTypeIds,
    });

    const currentShifts = (shifts || []).filter((s: any) => {
        const f = Date.parse((s as any)?.fromDate as string);
        const t = Date.parse(((s as any)?.toDate as string) ?? (s as any)?.fromDate as string);
        return isFinite(f) && isFinite(t) && f <= nowMs && t >= nowMs;
    });

    // Načtená data z širokého rozsahu (celý výrobní den + další den) pro planned a unplanned odstávky
    const plannedUnion = buildUnionIntervalsByWorkplace(planned as any[], fromIso, toIso);
    const unplannedUnion = buildUnionIntervalsByWorkplace(unplanned as any[], fromIso, toIso);

    const resolveCt = await buildCycleTimeResolver(shifts || []);
    const locChainsByWp = buildLocationChainsForShifts(shifts || []);

    // Načteme všechny scrap records jednou pro celý rozsah všech aktuálních směn
    const allScrapRecords: any[] = await ScrapRecordsRepository.fetchShiftScrapRecords(
        fromIso, 
        cutoffIso,
        {
            locationIds: filters?.locationIds,
            workplaceIds: filters?.workplaceIds,
            projectIds: filters?.projectIds,
            shiftTypeIds: filters?.shiftTypeIds,
            shiftTeamTypeIds: filters?.shiftTeamTypeIds,
        }
    );

    const result = new Map<string | null, PanelOeeComponents>();

    for (const s of currentShifts as any as ShiftModel[]) {
        const wid: string | null = (s as any).workplaceId ?? (s as any)?.workplace?.id ?? null;
        if (!wid) continue;

        const sFromMs = Date.parse(s.fromDate as string);
        const sToMs = Date.parse((s.toDate as string) ?? (s.fromDate as string));
        const aFromMs = Math.max(sFromMs, fromMs);
        const aToMsCutoff = Math.min(sToMs, cutoffMs);
        
        // Pokud cutoff je před začátkem směny nebo rovno, přeskočit (nemáme žádnou ukončenou hodinu)
        if (!(aToMsCutoff > aFromMs)) continue;

        // Všechny metriky (Dostupnost, Výkonnost, Kvalita) – okno: začátek směny → poslední celá hodina
        const shiftMinutesCutoff = Math.max(0, Math.floor((aToMsCutoff - aFromMs) / 60000));
        const plannedMinCutoff = sumOverlapMinutes(plannedUnion.get(wid), aFromMs, aToMsCutoff);
        let unplannedMinCutoff = sumOverlapMinutes(unplannedUnion.get(wid), aFromMs, aToMsCutoff);
        // Odečteme překryv plánovaných a neplánovaných odstávek
        const overlapMinCutoff = computeIntervalIntersectionMinutes(plannedUnion.get(wid), unplannedUnion.get(wid), aFromMs, aToMsCutoff);
        unplannedMinCutoff = Math.max(0, unplannedMinCutoff - overlapMinCutoff);
        const XCutoff = Math.max(0, shiftMinutesCutoff - plannedMinCutoff);

        // NOK se berou ze ShiftScrapRecords – filtr dle ID směny (přesný) a času z již načtených dat
        const scrapForShift = (allScrapRecords || []).filter((r: any) => {
            if ((r?.shiftId || null) !== ((s as any).id || null)) return false;
            const recordTimeMs = Date.parse(r?.recordTime || '');
            return isFinite(recordTimeMs) && recordTimeMs >= aFromMs && recordTimeMs <= aToMsCutoff;
        });

        // Součty OK/NOK do poslední celé hodiny, zároveň akumulace dle produktu pro CT
        const okByProduct = new Map<string, number>();
        const nokByProduct = new Map<string, number>();

        let totalOkCutoff = 0;
        let totalNokCutoff = 0;

        if (s && Array.isArray((s as any).shiftProductions)) {
            for (const p of (s as any).shiftProductions as any[]) {
                const pid: string | null = (p?.productId ?? p?.product?.id) || null;
                const recs: any[] = Array.isArray(p?.shiftProductionRecords) ? p.shiftProductionRecords : [];
                const okSum = recs
                    .filter((r) => {
                        const t = r?.recordTime ? Date.parse(r.recordTime) : aFromMs;
                        return isFinite(t) && t <= aToMsCutoff && t >= aFromMs;
                    })
                    .reduce((acc, r) => acc + (Number(r?.okCount ?? 0) || 0), 0);
                if (okSum > 0 && pid) okByProduct.set(pid, (okByProduct.get(pid) || 0) + okSum);
                totalOkCutoff += okSum;
            }
        }

        for (const r of scrapForShift) {
            const pid = (r?.product?.id || r?.productId) as string | null;
            const sc = Number(r?.scrapCount ?? 0) || 0;
            if (pid) nokByProduct.set(pid, (nokByProduct.get(pid) || 0) + sc);
            totalNokCutoff += sc;
        }

        // Ideal runtime podle CT z OK+NOK do cutoff
        const locChain = locChainsByWp.get(wid) || [];
        let idealRuntimeMinutesCutoff = 0;
        const productIds = new Set<string>([...okByProduct.keys(), ...nokByProduct.keys()]);
        for (const pid of productIds) {
            const ok = okByProduct.get(pid) || 0;
            const nok = nokByProduct.get(pid) || 0;
            const count = ok + nok;
            if (count <= 0) continue;
            const res = resolveCt({ workplaceId: wid, productId: pid, productCollectionId: null, locationChain: locChain });
            const ctMin = res.minutes;
            if (typeof ctMin === 'number' && ctMin > 0) {
                idealRuntimeMinutesCutoff += count * ctMin;
            }
        }

        // Omezíme idealRuntimeMinutesCutoff na max dostupný čas pro výrobu (XCutoff - neplánované odstávky)
        // aby nedošlo k přetečení do další směny při špatně nastavené normě
        const maxAvailableMinutesCutoff = Math.max(0, XCutoff - unplannedMinCutoff);
        idealRuntimeMinutesCutoff = Math.min(idealRuntimeMinutesCutoff, maxAvailableMinutesCutoff);

        // Použijeme computeOeeComponents pro konzistentní výpočet dostupnosti, výkonnosti a kvality
        // Všechny metriky počítáme do poslední celé hodiny (XCutoff, unplannedMinCutoff, totalOkCutoff, totalNokCutoff)
        
        const oeeComp = computeOeeComponents(
            totalOkCutoff,
            totalNokCutoff,
            0, // planCount - nepoužíváme
            XCutoff, // plannedMinutes
            unplannedMinCutoff, // downtimeMinutes
            idealRuntimeMinutesCutoff
        );
        
        const availability = oeeComp.availability;
        const performance = oeeComp.performance;
        const quality = oeeComp.quality;
        const oee = oeeComp.oee;

        result.set(wid, { 
            oee, 
            availability, 
            performance, 
            quality,
            // Surová data pro agregaci
            totalOk: totalOkCutoff,
            totalNok: totalNokCutoff,
            totalXMinutes: XCutoff,
            totalUnplannedMinutes: unplannedMinCutoff,
            totalIdealRuntimeMinutes: idealRuntimeMinutesCutoff
        });
    }

    return result;
}


